import { omit } from "lodash-es";
import React, { Suspense, useCallback, useMemo, useState } from "react";
import { uninitializedContextErrorFn } from "shared/errors/UninitializedContextError";

interface ConfirmationDialogContextValue {
  addDialogElement: (dialogId: string, node: React.ReactNode) => void;
  removeDialogElement: (dialogId: string) => void;
}

const throwUninitializedContextError = uninitializedContextErrorFn(
  "ConfirmationDialogContext",
);

export const ConfirmationDialogContext =
  React.createContext<ConfirmationDialogContextValue>({
    addDialogElement: throwUninitializedContextError,
    removeDialogElement: throwUninitializedContextError,
  });

export const ConfirmationDialogProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [dialogElements, setDialogElements] = useState<{
    [id: string]: React.ReactNode;
  }>({});

  const addDialogElement = useCallback(
    (dialogId: string, node: React.ReactNode) => {
      setDialogElements((prev) => ({
        ...prev,
        [dialogId]: node,
      }));
    },
    [setDialogElements],
  );

  const removeDialogElement = useCallback(
    (dialogId: string) => {
      setDialogElements((prev) => omit(prev, [dialogId]));
    },
    [setDialogElements],
  );

  const value = useMemo<ConfirmationDialogContextValue>(
    () => ({ addDialogElement, removeDialogElement }),
    [addDialogElement, removeDialogElement],
  );

  return (
    <ConfirmationDialogContext.Provider value={value}>
      {children}
      <Suspense fallback={null}>
        {Object.entries(dialogElements).map(([id, element]) => (
          <React.Fragment key={id}>{element}</React.Fragment>
        ))}
      </Suspense>
    </ConfirmationDialogContext.Provider>
  );
};
